import { translateBackend } from '@assets/i18n/config';
import backgroundImage from '@assets/images/brand_background_dark.png';
import { styled } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { AppBar, Toolbar } from '@ovComponents/2-component/appBar/appBar';
import { Button } from '@ovComponents/2-component/button/button';
import { useLayoutTokens } from '@ovComponents/4-module/layout/layout.tokens';
import OrganizationLogo from '@ovComponents/4-module/layout/organizationLogo';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

export const Anchor = styled('a')(() => ({
  color: 'white',
  '&:visited': {
    color: 'white',
  },
  '&:hover': {
    filter: 'brightness(85%)',
  },
}));

const ErrorBoundary = () => {
  const { t } = useTranslation(['errorBoundaries.json', 'workflowCompletions']);
  const { activeOrganization } = useContext(UserContext);

  const { comp } = useThemeTokens(useLayoutTokens());
  const SHOW_NAVBAR = activeOrganization.errorPageShowNavbar ?? false;
  const ERROR_PAGE_BACKGROUND_COLOR =
    activeOrganization.errorPageBackgroundColor;
  const ERROR_PAGE_BACKGROUND_IMAGE =
    activeOrganization.errorPageBackgroundImage ?? backgroundImage;
  const ERROR_PAGE_TITLE = activeOrganization.errorPageMessage
    ? translateBackend(activeOrganization.errorPageMessage)
    : t('errorBoundaries:base.title');
  const ERROR_PAGE_DESCRIPTION = activeOrganization.errorPageDescription
    ? translateBackend(activeOrganization.errorPageDescription)
    : t('errorBoundaries:base.subtitle');
  const ERROR_PAGE_BUTTON_TEXT = activeOrganization.errorPageButtonText
    ? translateBackend(activeOrganization.errorPageButtonText)
    : t('errorBoundaries:base.redirectLabel');
  const SUPPORT_URL =
    translateBackend(activeOrganization.translatedSupportUrl) ??
    'https://onevest.zendesk.com';
  const SHOW_HELP_CENTER_LINK =
    activeOrganization.enableResourcesAndArticles ?? false;

  return (
    <>
      {SHOW_NAVBAR && (
        <AppBar
          position='fixed'
          sx={{
            width: { md: '100%' },
            transition: 'all 0.3s',
            backgroundColor: {
              xs: comp.layout.backgroundColor,
              md: comp.layout.backgroundColor,
            },
          }}
          elevation={0}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <OrganizationLogo textColor={comp.layout.textColor} />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <section
        style={{
          position: 'absolute',
          backgroundImage: `url(${ERROR_PAGE_BACKGROUND_IMAGE})`,
          backgroundColor: ERROR_PAGE_BACKGROUND_COLOR,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          color='white'
          gap={3}
          px={5}
        >
          <Typography
            variant='bodyLarge'
            sx={{
              fontSize: '56px',
              lineHeight: '64px',
              margin: 2,
              textAlign: 'center',
            }}
          >
            {ERROR_PAGE_TITLE}
          </Typography>
          <Typography
            variant='trimmedLarge'
            sx={{ fontWeight: '400', textAlign: 'center' }}
          >
            {ERROR_PAGE_DESCRIPTION}
          </Typography>
          <Button
            onClick={() => {
              window.location.href = '/';
            }}
            label={ERROR_PAGE_BUTTON_TEXT}
          />
          {SHOW_HELP_CENTER_LINK && (
            <Anchor href={SUPPORT_URL} color='white'>
              <Typography variant='labelLarge'>
                {t('errorBoundaries:base.helpCenterLink')}
              </Typography>
            </Anchor>
          )}
        </Box>
      </section>
    </>
  );
};

export default ErrorBoundary;
